import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { RestaurantComparisonResponse } from "../utils/RestoApiService";
import styles from "./ComparisonBadges.module.css";

interface ComparisonBadgesProps {
  comparison: RestaurantComparisonResponse | null;
  isLoading: boolean;
}

/**
 * Calculate "better than X%" - the percentage of restaurants this one beats
 */
function calcBetterThan(rank: number, total: number): number {
  if (total <= 1) return 100;
  return Math.round(((total - rank) / (total - 1)) * 100);
}

/**
 * Get quality tier based on percentile beaten
 */
function getQualityTier(betterThan: number): { labelKey: string; class: string } {
  if (betterThan >= 90) return { labelKey: "comparison.quality.excellent", class: "excellent" };
  if (betterThan >= 70) return { labelKey: "comparison.quality.good", class: "good" };
  if (betterThan >= 40) return { labelKey: "comparison.quality.average", class: "average" };
  return { labelKey: "comparison.quality.belowAvg", class: "poor" };
}

const ComparisonBadges: React.FC<ComparisonBadgesProps> = ({
  comparison,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.skeleton} />
      </div>
    );
  }

  if (!comparison) return null;

  const { cityComparison, postalCodeComparison, categoryComparison, scoreBreakdown } = comparison;

  const hasData = cityComparison || postalCodeComparison || categoryComparison;
  if (!hasData) return null;

  const historyStatus = getHistoryStatus(scoreBreakdown.historyItems);

  return (
    <div className={styles.container}>
      {/* Rankings Grid */}
      <div className={styles.grid}>
        {cityComparison && (
          <RankItem
            labelKey="comparison.labels.city"
            place={cityComparison.cityName}
            rank={cityComparison.rank}
            total={cityComparison.totalRestaurants}
            t={t}
          />
        )}

        {postalCodeComparison && (
          <RankItem
            labelKey="comparison.labels.area"
            place={postalCodeComparison.postalCode}
            rank={postalCodeComparison.rank}
            total={postalCodeComparison.totalRestaurants}
            t={t}
          />
        )}

        {categoryComparison && (
          <RankItem
            labelKey="comparison.labels.category"
            place={categoryComparison.category}
            rank={categoryComparison.rank}
            total={categoryComparison.totalInCategory}
            t={t}
          />
        )}
      </div>

      {/* Score Breakdown Toggle */}
      <button
        className={styles.detailsToggle}
        onClick={() => setShowDetails(!showDetails)}
        aria-expanded={showDetails}
      >
        <span>{t("comparison.whyThisScore")}</span>
        <svg
          className={`${styles.chevron} ${showDetails ? styles.chevronOpen : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Score Breakdown Panel */}
      {showDetails && (
        <div className={styles.details}>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>{t("comparison.breakdown.forks")}</span>
            <span className={styles.detailValue}>{scoreBreakdown.forksRaw}/5</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>{t("comparison.breakdown.history")}</span>
            <span className={`${styles.detailValue} ${styles[historyStatus.class]}`}>
              {t(historyStatus.textKey)}
            </span>
          </div>
          {scoreBreakdown.totalReports > 0 && (
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t("comparison.breakdown.reports")}</span>
              <span className={styles.detailValue}>
                {scoreBreakdown.totalReports}
                {scoreBreakdown.averageRating > 0 && ` (${scoreBreakdown.averageRating.toFixed(1)} avg)`}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Individual rank item component
 */
interface RankItemProps {
  labelKey: string;
  place: string;
  rank: number;
  total: number;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const RankItem: React.FC<RankItemProps> = ({ labelKey, place, rank, total, t }) => {
  const betterThan = calcBetterThan(rank, total);
  const quality = getQualityTier(betterThan);

  return (
    <div className={styles.rankItem}>
      <div className={styles.rankHeader}>
        <span className={styles.rankLabel}>{t(labelKey)}</span>
        <span className={styles.rankPlace}>{place}</span>
      </div>
      <div className={styles.rankValue}>
        <span className={styles.rankNumber}>{rank}</span>
        <span className={styles.rankTotal}>/ {total.toLocaleString()}</span>
      </div>
      <div className={styles.rankBar}>
        <div
          className={`${styles.rankBarFill} ${styles[quality.class]}`}
          style={{ width: `${betterThan}%` }}
        />
      </div>
      <div className={styles.rankFooter}>
        <span className={styles.rankBetterThan}>
          {betterThan <= 50
            ? t("comparison.inWorst", { percent: Math.max(betterThan, 1) })
            : t("comparison.inBest", { percent: Math.max(100 - betterThan, 1) })
          }
        </span>
        <span className={`${styles.rankQuality} ${styles[quality.class]}`}>{t(quality.labelKey)}</span>
      </div>
    </div>
  );
};

function getHistoryStatus(historyItems: string[]): { textKey: string; class: string } {
  if (historyItems.length === 0) {
    return { textKey: "comparison.historyStatus.noData", class: "neutral" };
  }

  const hasWarning = historyItems.some((item) =>
    ["WARNING", "FINE", "PV"].includes(item)
  );
  if (hasWarning) {
    return { textKey: "comparison.historyStatus.hasIssues", class: "warning" };
  }

  const hasPassed = historyItems.includes("PASSED_INSPECTION");
  if (hasPassed) {
    return { textKey: "comparison.historyStatus.clean", class: "good" };
  }

  return { textKey: "comparison.historyStatus.mixed", class: "neutral" };
}

export default ComparisonBadges;
